<template>
    <div class="step" :class="{ 'is-embedded': embedded }">
        <div class="column">
            <BasePageHeader
                :variant="embedded ? 'step' : 'page'"
                title="How do you work?"
                subtitle="No right answers — go with your gut. Your answers save as you go."
                no-border
                subtitle-below>
                <template #eyebrow><p>Perspectives</p></template>
            </BasePageHeader>

            <PerspectiveSurvey
                ref="survey"
                class="survey"
                layout="embedded"
                :questions="assessment.questions"
                :saved-responses="assessment.saved_responses"
                :session-id="assessment.session_id"
                submit-label="See my results"
                @submitted="emit('submitted')">
                <!-- Embedded setup already provides an exit through its persistent rail; the linear flow
                     needs this skip control to proceed without answering.
                     -->
                <template v-if="!embedded" #escape>
                    <BaseButton theme="tertiary" class="step-skip" :disabled="busy" @click="emit('skip')">Skip for now</BaseButton>
                </template>
            </PerspectiveSurvey>
        </div>
    </div>
</template>

<script setup lang="ts">
import { fetchApi } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import PerspectiveSurvey from "~vue/Perspective/PerspectiveSurvey.vue";
import { onboardingDevToolsStore, setAssessmentFillAvailable } from "~vue/stores/onboardingDevToolsStore";
import { computed, onBeforeUnmount, useTemplateRef, watch } from "vue";

import type { AssessmentState } from "./types";

const { embedded = false, assessment, busy } = defineProps<{ embedded?: boolean; assessment: AssessmentState; busy: boolean }>();

const emit = defineEmits<{ submitted: []; skip: [] }>();

const survey = useTemplateRef<InstanceType<typeof PerspectiveSurvey>>("survey");

/*
 * Staff testing bypasses sixty manual answers through the Perspectives demo endpoint, which persists them
 * before submission.
 */
watch(
    () => onboardingDevToolsStore.fillAssessmentToken,
    async (token) => {
        if (token === 0) {
            return;
        }
        const response = await fetchApi(`/v1/perspectives/sessions/${assessment.session_id}/fill-remaining`, { method: "POST" });
        if (!response.ok) {
            return;
        }
        const payload = (await response.json()) as { filled_responses?: Record<string, number> };
        survey.value?.adoptResponses(payload.filled_responses ?? {});
    },
);

const fillAvailable = computed(() => assessment.questions.length > 0);
watch(fillAvailable, (available) => setAssessmentFillAvailable(available), { immediate: true });

onBeforeUnmount(() => setAssessmentFillAvailable(false));
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .step {
        @apply px-6 py-6 md:py-8;
    }
    .step.is-embedded {
        @apply p-0;
    }

    .column {
        @apply mx-auto w-full max-w-168;
    }
    .step.is-embedded .column {
        @apply mx-0 max-w-none;
    }

    .survey {
        @apply mt-6;
    }

    /* The host already sets the page gutter, and the survey's own would indent it
       against the heading directly above it. */
    .survey :deep(.progress),
    .survey :deep(.footer) {
        @apply px-0;
    }
    .survey :deep(.scroll-container) > * {
        @apply px-0;
    }
}
</style>
